import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { SocialButton } from "../components/SocialButton";
import { useAuthActions } from "../hooks/auth/useAuthActions";
import MainLogo from "../components/Logo";
import designTokens from "../styles/designTokens";
import { useAuth } from "../contexts/AuthContext";
import Privacy from "../components/Privacy";
import Terms from "../components/Terms";
import Toast from "../components/Toast";
import figmaPluginAuthCompleteIcon from "../assets/figma-plugin-auth-complete.svg";
import { completeFigmaPluginAuthSession } from "../services/figmaPluginAuthClient";
import { isFigmaPluginPopupContext } from "../services/authService";
import LoginPage from "./LOGIN";

const ACCOUNT_SWITCH_READY_PREFIX = "viewr_figma_plugin_switch_ready:";
const ACCOUNT_SWITCH_PROVIDER_PREFIX =
  "viewr_figma_plugin_switch_provider_started:";
const SESSION_EXPIRED_MESSAGE =
  "Your sign-in session expired. Go back to the Figma plugin and try again.";

const accountSwitchReadyKey = (sessionId: string) =>
  `${ACCOUNT_SWITCH_READY_PREFIX}${sessionId}`;
const accountSwitchProviderKey = (sessionId: string) =>
  `${ACCOUNT_SWITCH_PROVIDER_PREFIX}${sessionId}`;

const readAccountSwitchAttemptStartedAt = (sessionId: string): number | null => {
  const raw = window.sessionStorage.getItem(accountSwitchProviderKey(sessionId));
  const parsed = raw ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const clearAccountSwitchState = (sessionId: string) => {
  window.sessionStorage.removeItem(accountSwitchReadyKey(sessionId));
  window.sessionStorage.removeItem(accountSwitchProviderKey(sessionId));
};

/**
 * `/figma-plugin-auth` — Figma Plugin 로그인 완료 페이지.
 */
const FigmaPluginAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    user: authUser,
    isAuthenticated,
    loading: authLoading,
    redirectSignInResult,
  } = useAuth();
  const {
    loginGoogle,
    loginApple,
    loginMicrosoft,
    error: socialAuthError,
  } = useAuthActions();

  const isPopup =
    searchParams.get("popup") === "1" || isFigmaPluginPopupContext();
  const embedded =
    !isPopup &&
    (searchParams.get("embedded") === "1" || window.self !== window.top);
  const forceAccountSelection =
    searchParams.get("force_account_selection") === "1";
  const sidFromUrl = searchParams.get("session");

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authComplete, setAuthComplete] = useState(false);
  // 플러그인과 Studio에 서로 다른 계정으로 로그인하면 등록한 Prototype이 Library에
  // 보이지 않는다. 어떤 계정으로 연결됐는지 이 화면에서 확인할 수 있게 남긴다.
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showSessionExpiredToast, setShowSessionExpiredToast] = useState(false);
  const [accountSwitchReady, setAccountSwitchReady] = useState(
    !forceAccountSelection,
  );
  const [accountSwitchAttemptStartedAt, setAccountSwitchAttemptStartedAt] =
    useState<number | null>(() =>
      sidFromUrl ? readAccountSwitchAttemptStartedAt(sidFromUrl) : null,
    );
  const [accountSwitchAuthenticationSucceeded, setAccountSwitchAuthenticationSucceeded] =
    useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const completeOnce = useRef(false);
  const sessionBootstrapped = useRef(false);

  const hasRegularUser = Boolean(authUser && authUser.isGuest !== true);
  const hasCompletableFirebaseSession =
    hasRegularUser && Boolean(auth.currentUser);

  const prepareAccountSwitch = useCallback((id: string) => {
    setAuthError(null);
    window.sessionStorage.setItem(accountSwitchReadyKey(id), "1");
    window.sessionStorage.removeItem(accountSwitchProviderKey(id));
    setAccountSwitchAttemptStartedAt(null);
    setAccountSwitchAuthenticationSucceeded(false);
    completeOnce.current = false;
    setAccountSwitchReady(true);
  }, []);

  useEffect(() => {
    if (sessionBootstrapped.current) return;
    sessionBootstrapped.current = true;

    const bootstrapSession = async () => {
      if (!sidFromUrl) {
        setLoading(false);
        return;
      }

      if (forceAccountSelection) {
        const switchReady =
          window.sessionStorage.getItem(accountSwitchReadyKey(sidFromUrl)) ===
          "1";
        if (switchReady) {
          setAccountSwitchReady(true);
        } else {
          prepareAccountSwitch(sidFromUrl);
        }
      }

      setSessionId(sidFromUrl);
      setLoading(false);
    };

    void bootstrapSession();
  }, [forceAccountSelection, prepareAccountSwitch, sidFromUrl]);

  const runComplete = useCallback(
    async (id: string) => {
      if (completeOnce.current) return;
      const firebaseUser = auth.currentUser;
      if (!hasRegularUser || !firebaseUser) return;

      completeOnce.current = true;
      try {
        const idToken = await firebaseUser.getIdToken(true);
        const res = await completeFigmaPluginAuthSession(id, idToken);
        if (!res.ok) {
          setShowSessionExpiredToast(true);
          return;
        }
        if (forceAccountSelection) {
          clearAccountSwitchState(id);
        }
        setConnectedEmail(firebaseUser.email?.trim() || null);
        setAuthComplete(true);
        const completeMessage = {
          type: "viewr-figma-plugin-auth-complete",
          sessionId: id,
        };
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(completeMessage, "*");
        }
        if (embedded && window.parent !== window) {
          window.parent.postMessage(completeMessage, "*");
        }
      } catch (e) {
        completeOnce.current = false;
        const msg =
          e instanceof Error ? e.message : "Session completion failed";
        setAuthError(msg);
      }
    },
    [embedded, forceAccountSelection, hasRegularUser],
  );

  const beginAccountSwitchAuthentication = useCallback(() => {
    if (!sessionId) return;
    const startedAt = Date.now();
    window.sessionStorage.setItem(
      accountSwitchProviderKey(sessionId),
      String(startedAt),
    );
    completeOnce.current = false;
    setAccountSwitchAttemptStartedAt(startedAt);
    setAccountSwitchAuthenticationSucceeded(false);
  }, [sessionId]);

  const completeAccountSwitchAuthentication = useCallback(() => {
    setAccountSwitchAuthenticationSucceeded(true);
  }, []);

  const accountSwitchRedirectMatches = Boolean(
    forceAccountSelection &&
      accountSwitchAttemptStartedAt &&
      redirectSignInResult &&
      redirectSignInResult.completedAt >= accountSwitchAttemptStartedAt &&
      auth.currentUser?.uid === redirectSignInResult.uid,
  );

  const canCompleteAccountSwitch = Boolean(
    forceAccountSelection &&
      (accountSwitchAuthenticationSucceeded || accountSwitchRedirectMatches),
  );

  useEffect(() => {
    if (!authComplete || !isPopup) return;
    const timer = window.setTimeout(() => {
      window.close();
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [authComplete, isPopup]);

  const pageSx = isPopup
    ? {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }
    : embedded
      ? {
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column" as const,
          alignItems: "center",
          justifyContent: "flex-start",
          p: 2,
          bgcolor: designTokens.fill.shape.grey30.dark,
        }
      : {
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column" as const,
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        };

  const useCompactUi = embedded && !isPopup;
  const buttonWidth = useCompactUi ? "100%" : 467;

  const openLoginInBrowser = useCallback(() => {
    const url = window.location.href;
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: "viewr-figma-plugin-open-external", url },
        "*",
      );
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const displayError = authError || socialAuthError;

  const selectProvider = useCallback(
    async (login: () => Promise<void>) => {
      completeOnce.current = false;
      setAuthError(null);
      try {
        await login();
        // Firebase의 auth observer가 먼저 실행되는 경우와 login promise가 먼저
        // 끝나는 경우를 모두 지원한다. runComplete의 guard가 중복 완료를 막는다.
        if (sessionId && auth.currentUser) {
          await runComplete(sessionId);
        }
      } catch (error) {
        if (error instanceof Error && error.message) {
          setAuthError(error.message);
        }
      }
    },
    [runComplete, sessionId],
  );

  useEffect(() => {
    if (!sessionId) return;

    const unsub = onAuthStateChanged(auth, (user) => {
      if (
        user &&
        hasRegularUser &&
        (!forceAccountSelection || canCompleteAccountSwitch)
      ) {
        void runComplete(sessionId);
      }
    });

    if (
      auth.currentUser &&
      hasRegularUser &&
      (!forceAccountSelection || canCompleteAccountSwitch)
    ) {
      void runComplete(sessionId);
    }

    return () => unsub();
  }, [
    canCompleteAccountSwitch,
    forceAccountSelection,
    hasRegularUser,
    sessionId,
    runComplete,
  ]);

  if (loading || authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!sessionId) {
    return (
      <Box sx={{ p: 4, maxWidth: 480, mx: "auto" }}>
        <Alert severity="error">
          Missing session. Return to the Figma plugin and try again.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/login")}>
          Sign in
        </Button>
      </Box>
    );
  }

  if (authComplete) {
    return (
      <Box
        sx={{
          ...pageSx,
          minHeight: "100dvh",
          justifyContent: "center",
          textAlign: "center",
          boxSizing: "border-box",
          bgcolor: designTokens.fill.background.grey20.dark,
          ...(useCompactUi ? {} : { p: "40px" }),
        }}
      >
        {useCompactUi ? (
          <Typography
            sx={{
              ...designTokens.typography.styles.headline1,
              color: designTokens.fill.text.common100.dark,
            }}
          >
            Signing you in…
          </Typography>
        ) : (
          <Stack
            data-testid="figma-plugin-auth-connected-content"
            alignItems="center"
            sx={{ gap: "40px" }}
          >
            <Box
              component="img"
              data-testid="figma-plugin-auth-complete-icon"
              src={figmaPluginAuthCompleteIcon}
              alt=""
              aria-hidden="true"
              sx={{
                width: 64,
                height: 64,
                aspectRatio: "1 / 1",
                display: "block",
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
            <Stack
              data-testid="figma-plugin-auth-connected-text"
              alignItems="center"
              sx={{ gap: "12px" }}
            >
              <Typography
                component="h1"
                sx={{
                  ...designTokens.typography.styles.viewrStudio
                    .display1Semibold,
                  m: 0,
                  fontWeight: 600,
                  color: designTokens.fill.text.grey90.dark,
                  whiteSpace: "nowrap",
                }}
              >
                Connected to View R
              </Typography>
              {connectedEmail && (
                <Typography
                  data-testid="figma-plugin-auth-account"
                  sx={{
                    ...designTokens.typography.styles.viewrStudio
                      .headingRegular,
                    m: 0,
                    color: designTokens.fill.text.grey90.dark,
                  }}
                >
                  {connectedEmail}
                </Typography>
              )}
              <Typography
                data-testid="figma-plugin-auth-connected-caption"
                sx={{
                  ...designTokens.typography.styles.viewrStudio.headingRegular,
                  m: 0,
                  color: designTokens.fill.text.grey80.dark,
                  textAlign: "center",
                }}
              >
                You can close this window and return to Figma.
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    );
  }

  if (
    accountSwitchReady &&
    (forceAccountSelection || !hasCompletableFirebaseSession)
  ) {
    return (
      <>
        <LoginPage
          variant="default"
          allowGuest={false}
          onAuthenticationStart={
            forceAccountSelection ? beginAccountSwitchAuthentication : undefined
          }
          onAuthenticationSuccess={
            forceAccountSelection
              ? completeAccountSwitchAuthentication
              : undefined
          }
        />
        <Toast
          type="error"
          message={SESSION_EXPIRED_MESSAGE}
          open={showSessionExpiredToast}
          onClose={() => setShowSessionExpiredToast(false)}
          showCloseButton={false}
        />
      </>
    );
  }

  return (
    <Box sx={pageSx}>
      {!useCompactUi && <MainLogo />}
      <Typography
        component="h1"
        sx={{
          mt: useCompactUi ? 1 : 4,
          mb: 1,
          ...(useCompactUi
            ? designTokens.typography.styles.headline1
            : designTokens.typography.styles.viewrStudio.headingMedium),
          color: designTokens.fill.text.common100.dark,
        }}
      >
        {forceAccountSelection
          ? "Choose a View R account"
          : "Sign in to View R"}
      </Typography>
      {!useCompactUi && (
        <Typography
          sx={{
            mb: 3,
            color: designTokens.fill.text.grey60.dark,
            textAlign: "center",
            maxWidth: 360,
          }}
        >
          {forceAccountSelection
            ? "Select the account you want to connect to the Figma plugin."
            : "Sign in to connect the Figma plugin to View R Studio."}
        </Typography>
      )}
      {useCompactUi && (
        <Typography
          sx={{
            mb: 2,
            color: designTokens.fill.text.grey60.dark,
            textAlign: "center",
            fontSize: "13px",
            lineHeight: 1.4,
            maxWidth: 320,
          }}
        >
          Choose a provider below. A sign-in popup will open — allow popups if
          prompted.
        </Typography>
      )}

      {displayError && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
          {displayError}
        </Alert>
      )}

      {useCompactUi && displayError && (
        <Button
          variant="outlined"
          size="small"
          onClick={openLoginInBrowser}
          sx={{ mb: 2, textTransform: "none" }}
        >
          Open sign-in in browser instead
        </Button>
      )}

      {isAuthenticated && !authComplete && !forceAccountSelection && (
        <CircularProgress size={32} sx={{ my: 2 }} />
      )}

      {(!isAuthenticated || forceAccountSelection) && accountSwitchReady && (
        <Stack
          spacing={useCompactUi ? 1 : 1.5}
          sx={{
            width: "100%",
            maxWidth: useCompactUi ? "100%" : 320,
            alignItems: "center",
          }}
        >
          <SocialButton
            onClick={() => void selectProvider(loginGoogle)}
            src="/Images/Icons/Google.png"
            alt="Google"
            width={buttonWidth}
          >
            Continue with Google
          </SocialButton>
          <SocialButton
            onClick={() => void selectProvider(loginMicrosoft)}
            src="/Images/Icons/Microsoft.png"
            alt="Microsoft"
            width={buttonWidth}
          >
            Continue with Microsoft
          </SocialButton>
          <SocialButton
            onClick={() => void selectProvider(loginApple)}
            src="/Images/Icons/Apple.png"
            darkSrc="/Images/Icons/Social Login_apple_dark.png"
            lightSrc="/Images/Icons/Social Login_apple_light.png"
            alt="Apple"
            width={buttonWidth}
          >
            Continue with Apple
          </SocialButton>
        </Stack>
      )}

      {!useCompactUi && (
        <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
          <Button size="small" onClick={() => setOpenTerms(true)}>
            Terms
          </Button>
          <Button size="small" onClick={() => setOpenPrivacy(true)}>
            Privacy
          </Button>
        </Box>
      )}

      <Terms open={openTerms} onClose={() => setOpenTerms(false)} />
      <Privacy open={openPrivacy} onClose={() => setOpenPrivacy(false)} />
      <Toast
        type="error"
        message={SESSION_EXPIRED_MESSAGE}
        open={showSessionExpiredToast}
        onClose={() => setShowSessionExpiredToast(false)}
        showCloseButton={false}
      />
    </Box>
  );
};

export default FigmaPluginAuthPage;
